import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import imagen1 from '../assets/images/ContactPage/contactuspageimg1.webp';
import imagen2 from '../assets/images/ContactPage/contactuspageimg2.webp';
import { supabase } from '../utils/supabaseClient';

//-- Proyecto diseñado, programado, testeado, ejecutado y publicado por Alex Castillo.
//-- Autor: Alex Castillo © 2026 Alex Castillo – Todos los derechos reservados.

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    nit: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { data, error } = await supabase
      .from('contactos')
      .insert([{
        name: formData.name,
        nit: formData.nit,
        company: formData.company,
        email: formData.email,
        phone: formData.phone,
        message: formData.message
      }])
      .select();

    console.log("DATA:", data);
    console.log("ERROR:", error);

    if (error) {
      console.error(error);
      alert("Error, mensaje NO enviado. Comuniquese con el área de soporte marcando al +57 3183473820.");
      return;
    }

    alert(`¡Mensaje enviado y en tramite!\nGracias ${formData.name}, pronto nos pondremos en contacto contigo.`);

    setFormData({
      name: '',
      nit: '',
      company: '',
      email: '',
      phone: '',
      message: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const images = [imagen1, imagen2];

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [images.length]);

  const particles = Array.from({ length: 40 });

  return (
    <div className="relative min-h-screen py-12 overflow-hidden">
      {/* SLIFER FADE */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0">
          {images.map((img, index) => {
            const isActive = index === currentIndex;

            return (
              <motion.div
                key={index}
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${img})` }}
                initial={{ opacity: 0, scale: 1.02, filter: "blur(8px)" }}
                animate={
                  isActive
                    ? {
                      opacity: 1,
                      scale: 1,
                      filter: "blur(0px)"
                    }
                    : {
                      opacity: 0,
                      scale: 1.05,
                      filter: "blur(6px)"
                    }
                }
                transition={{
                  duration: 2.5,
                  ease: [0.4, 0, 0.2, 1] // easing crossfade cinematográfico
                }}
              />
            );
          })}
        </div>

        {/* OVERLAY GRADIENTE (SUAVIZADO ARRIBA → ABAJO) */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/50 via-black/70 to-black z-10" />
    
      </div>

      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-2xl font-bold mb-6 text-white">Información de Contacto</h2>
            
            <div className="space-y-6">
              <motion.div 
                className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-xl shadow-gray-700 hover:shadow-2xl"
                whileHover={{ scale: 1.05 }}
              >
                <div className="bg-gradient-to-br from-blue-500 to-blue-800 p-3 rounded-full">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">Dirección</h3>
                  <p className="text-gray-600">
                    Calle 75A # 61 - 44, San Fernando<br/>
                    Bogotá, Colombia
                  </p>
                </div>
              </motion.div>

              <motion.div 
                className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-xl shadow-gray-700 hover:shadow-2xl"
                whileHover={{ scale: 1.05 }}
              >
                <div className="bg-gradient-to-br from-blue-500 to-blue-800 p-3 rounded-full">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">Asesores Comerciales</h3>
                  <p className="text-gray-600">
                    Ventas Bogotá 1: +57 (318) 365 2386<br/>
                    Ventas Bogotá 2: +57 (300) 558 3994<br/>
                    Ventas Medellín: +57 (304) 377 7120
                  </p>
                </div>
              </motion.div>

              <motion.div 
                className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-xl shadow-gray-700 hover:shadow-2xl"
                whileHover={{ scale: 1.05 }}
              >
                <div className="bg-gradient-to-br from-blue-500 to-blue-800 p-3 rounded-full">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">Correos Electrónicos</h3>
                  <p className="text-gray-600">
                    Gerencia: l.segura@inverdronjgw.com<br/>
                    General: info@inverdronjgw.com
                  </p>
                </div>
              </motion.div>

              <motion.div 
                className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-xl shadow-gray-700 hover:shadow-2xl"
                whileHover={{ scale: 1.05 }}
              >
                <div className="bg-gradient-to-br from-blue-500 to-blue-800 p-3 rounded-full">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">Horarios de Atención</h3>
                  <p className="text-gray-600">
                    Lunes a Viernes: 7:30 AM - 5:00 PM<br/>
                    Sábados: 8:30 AM - 12:00 PM
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="bg-white rounded-2xl p-8 shadow-xl shadow-gray-700 hover:shadow-2xl">
              <h2 className="text-2xl font-bold mb-6 text-gray-800">Envíanos un Mensaje</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Nombre Completo</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Nombre y Apellidos completos"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">NIT - CC</label>
                  <input
                    type="text"
                    name="nit"
                    value={formData.nit}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Tu NIT, cédula o ID"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Empresa</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Empresa a la que perteneces"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Correo Electrónico</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="tu@email.com"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Teléfono</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="+EXT 3XXXXXXXX"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Mensaje</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                    placeholder="Cuéntanos en qué podemos ayudarte..."
                  />
                </div>

                <motion.button
                  type="submit"
                  className="w-full bg-gradient-to-r from-yellow-200 to-yellow-800 text-white py-3 px-6 rounded-lg font-semibold flex items-center justify-center gap-2 hover:shadow-xl transition-shadow"
                  whileHover={{ scale: 1.06 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Send className="w-5 h-5" />
                  Enviar Mensaje
                </motion.button>
              </form>
            </div>
          </motion.div>
        </div>

        <motion.div
          className="mt-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-center mb-8 text-white">Nuestra Ubicación</h2>
          <div className="bg-white rounded-2xl p-4 shadow-xl shadow-gray-700 hover:shadow-2xl">
            <div className="w-full h-96 bg-gray-200 rounded-xl flex items-center justify-center">
              <div className="w-full h-96 rounded-xl overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1988.2684452160279!2d-74.0812497154197!3d4.676317989634035!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8e3f9b02c467acc5%3A0xdf725d17c3f30a00!2sCl.%2075A%20%2361-44%2C%20Bogot%C3%A1!5e0!3m2!1ses!2sco!4v1771819319525!5m2!1ses!2sco"
                  width="1500"
                  height="450"
                  style={{ border: 0 }}
                  allowfullscreen=""
                  loading="lazy"
                  referrerpolicy="no-referrer-when-downgrade"
                  className="rounded-xl"
                ></iframe>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactPage;

//-- Proyecto diseñado, programado, testeado, ejecutado y publicado por Alex Castillo.
//-- Autor: Alex Castillo © 2026 Alex Castillo – Todos los derechos reservados.